import { Box, IconButton, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import type { ReactElement } from "react"
import React from "react"

import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import type { IncludeWithEnum, Instruction } from "@/graphql/codegen/graphql"
import { ApplicableToEnum, TransportationModeEnum } from "@/graphql/codegen/graphql"
import { InstructionActions } from "@/screens/ManageProducts/components/Instructions/components/rowActions/InstructionActions"
import { InstructionDialogs } from "@/screens/ManageProducts/components/Instructions/types"
import {
  APPLICABLE_TO_ALL_LABEL,
  TRANSPORTATION_MODE_ALL_LABEL,
  applicableToLabelMap,
  includeWithLabelMap,
  transportationModeLabelMap,
} from "@/screens/ManageProducts/components/Instructions/utils/instructionDataMap"
import { createStatusMap } from "@/utils/statusMap"

const instructionStatusMap = createStatusMap<boolean, "success" | "error">([
  [true, "success"],
  [false, "error"],
])

interface UseInstructionsColumnsProps {
  setSelectedInstruction: (instruction: Instruction | null) => void
  setDialogOpen: (dialog: InstructionDialogs | null) => void
  refetchProductsList: (id: number) => void
}

export const renderDescriptionCell = ({ row }: CellContext<Instruction, unknown>): ReactElement => {
  const instructionImageFile = row.original?.file

  return (
    <Box className='flex items-center'>
      {instructionImageFile?.originalName && instructionImageFile?.signedUrl && (
        <img
          className='mr-3'
          src={instructionImageFile.signedUrl}
          alt={instructionImageFile.originalName}
          width={48}
          height={48}
        />
      )}
      <Typography>{row.original.description}</Typography>
    </Box>
  )
}

export const renderStatusCell = ({ row, cell }: CellContext<Instruction, unknown>): ReactElement => {
  return (
    <StatusIndicator data-testid={`${cell.id}`} status={Boolean(row.original.isActive)} map={instructionStatusMap} />
  )
}

export const renderHazardousCell = ({ row }: CellContext<Instruction, unknown>): ReactElement => {
  return <Box className='flex flex-col'>{Boolean(row.original.isHazard) && <WarningOutlined color='error' />}</Box>
}

export const renderApplicableToCell = ({ row }: CellContext<Instruction, unknown>): ReactElement => {
  const instruction = row.original
  if (instruction.applicableTo.length === Object.values(ApplicableToEnum).length) {
    return <Box>{APPLICABLE_TO_ALL_LABEL}</Box>
  }
  const transformedApplicableTo = instruction.applicableTo.map((item) =>
    applicableToLabelMap.get(item as ApplicableToEnum)
  )
  return <Box>{transformedApplicableTo.join(", ")}</Box>
}

export const renderTransportationModeCell = ({ row }: CellContext<Instruction, unknown>): ReactElement => {
  const instruction = row.original
  if (instruction.transportationMode.length === Object.values(TransportationModeEnum).length) {
    return <Box>{TRANSPORTATION_MODE_ALL_LABEL}</Box>
  }
  const transformedTransportationMode = instruction.transportationMode.map((item) =>
    transportationModeLabelMap.get(item as TransportationModeEnum)
  )
  return <Box>{transformedTransportationMode.join(", ")}</Box>
}

export const renderIncludeWithCell = ({ row }: CellContext<Instruction, unknown>): ReactElement => {
  const instruction = row.original
  const transformedIncludeWith = instruction.includeWith.map((item) => includeWithLabelMap.get(item as IncludeWithEnum))
  return <Box>{transformedIncludeWith.join(", ")}</Box>
}

export const useInstructionsColumns = ({
  setSelectedInstruction,
  setDialogOpen,
  refetchProductsList,
}: UseInstructionsColumnsProps): ColumnDef<Instruction>[] => {
  const handleClickEditInstruction = (instruction: Instruction) => {
    setSelectedInstruction(instruction)
    setDialogOpen(InstructionDialogs.EDIT_INSTRUCTION)
  }

  return [
    {
      accessorKey: "isActive",
      header: "",
      cell: renderStatusCell,
      size: 24,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: "Instruction Name",
      sortingFn: "alphanumeric",
      enableSorting: true,
    },
    {
      accessorKey: "applicableTo",
      header: "Applicable To",
      cell: renderApplicableToCell,
      enableSorting: true,
    },
    {
      accessorKey: "transportationMode",
      header: "Transportation Mode",
      cell: renderTransportationModeCell,
      enableSorting: true,
    },
    {
      accessorKey: "includeWith",
      header: "Include With",
      cell: renderIncludeWithCell,
      enableSorting: true,
    },
    {
      accessorKey: "isHazard",
      header: "Hazardous",
      cell: renderHazardousCell,
      enableSorting: true,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => (
        <InstructionActions
          row={row}
          onEditInstruction={handleClickEditInstruction}
          refetchProductsList={refetchProductsList}
        />
      ),
      size: 50,
      enableSorting: false,
    },
    {
      accessorKey: "expander",
      enableSorting: false,
      header: "",
      cell: ({ row }: CellContext<Instruction, unknown>) => (
        <IconButton onClick={row.getToggleExpandedHandler()} className='p-1'>
          {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      ),
      size: 50,
    },
  ]
}
